import * as React from 'react';

import { formatPrice } from '@klareospace/pharos-lib-front-utils';
import { CURRENCIES, type Order } from '@klareospace/pharos-lib-types';
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@klareospace/pharos-lib-web-utils';

import { useInlineOrderFieldEdit } from '../hooks/use-inline-order-field-edit';
import type { OrderFieldEditState, OrderFieldLockReason } from '../hooks/use-order-editability';
import { EditableFieldWrapper } from './editable-field-wrapper';
import { InlineEditActions } from './inline-edit-actions';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PriceInlineEditorProps {
  readonly fieldState: OrderFieldEditState;
  readonly locale: string;
  readonly lockReason: OrderFieldLockReason | null;
  readonly order: Order;
}

type PriceDraft = { currency: string; value: number };

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isValidPrice(draft: PriceDraft): boolean {
  return Number.isFinite(draft.value) && draft.value >= 0;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function PriceInlineEditor({
  fieldState,
  locale,
  lockReason,
  order,
}: PriceInlineEditorProps): React.JSX.Element {
  const current = order.orderValue ?? { currency: CURRENCIES[0], value: 0 };

  const { draft, handleCancel, handleConfirm, isEditing, isPending, isValid, setDraft, startEdit } =
    useInlineOrderFieldEdit<PriceDraft>({
      buildPatch: (value) => ({ orderValue: value }),
      isValid: isValidPrice,
      order,
      successKey: 'drawer.edit.content.success',
      value: current,
    });

  if (isEditing) {
    return (
      <div className="mt-1 flex items-center gap-1">
        <Input
          className="h-7 flex-1 text-sm"
          disabled={isPending}
          min={0}
          onChange={(e): void => setDraft({ ...draft, value: Number(e.target.value) })}
          step="0.01"
          type="number"
          value={draft.value}
        />
        <Select
          disabled={isPending}
          onValueChange={(currency): void => setDraft({ ...draft, currency })}
          value={draft.currency}
        >
          <SelectTrigger className="h-7 w-20 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CURRENCIES.map((currency) => (
              <SelectItem key={currency} value={currency}>
                {currency}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <InlineEditActions isPending={isPending} isValid={isValid} onCancel={handleCancel} onConfirm={handleConfirm} />
      </div>
    );
  }

  return (
    <EditableFieldWrapper fieldState={fieldState} lockReason={lockReason} onEdit={startEdit}>
      <span className="text-sm text-foreground">{formatPrice(order.orderValue, locale)}</span>
    </EditableFieldWrapper>
  );
}
